<template>
  <q-card flat bordered>
    <div id="intercom-header" class="q-pa-md flex justify-between q-gutter-y-sm">
      <div>
        <div class="text-h6">Unit Prices Over Time</div>
        <div style="font-size: 12px">
          Analyze price trends and their correlation with new listings and lease closings.
        </div>
      </div>
      <div
        id="intercom-unit-prices-over-time-actions"
        class="flex q-gutter-sm"
        data-html2canvas-ignore
      >
        <q-select
          v-model="selectedBuilding"
          :options="buildingOptions"
          label="Select Building"
          class="ellipsis"
          style="width: 180px"
          transition-duration="100"
          flat
          outlined
          dense
          @update:model-value="updateBuilding"
        />
        <q-select
          v-model="metric"
          :options="metricOptions"
          label="Select Metric"
          style="width: 180px"
          transition-duration="100"
          class="ellipsis"
          flat
          outlined
          dense
          @update:model-value="updateMetric"
        />
      </div>
    </div>
    <q-separator />
    <q-card-section>
      <div id="intercom-unit-prices-over-time-content" class="chart-container">
        <div class="priceChart">
          <canvas ref="priceChart" class="priceChart" />
        </div>
        <div class="volumeChart">
          <canvas ref="volumeChart" class="volumeChart" />
        </div>
      </div>
    </q-card-section>
  </q-card>
</template>

<script lang="ts">
import { type PropType } from 'vue'
import { Chart, type ChartConfiguration } from 'chart.js/auto'
import type { Price, PropertyDetailsResponse, Availability, AvailabilityPeriod } from 'common'
import { unitTypeLabel } from 'common'
import { formatMoney, unitTypeColor } from '@/utils'
import { eachDayOfInterval, format, parseISO } from 'date-fns'
export default {
  name: 'PropertyCompareUnitPricesOverTime',
  props: {
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date }>,
      required: true
    },
    printingMode: {
      type: Boolean,
      default: false
    },
    expandedUnitHistory: {
      type: Object as PropType<{
        [unit_id: string]: {
          created_on: string
          history: Price
        }[]
      }>,
      required: true
    }
  },
  data() {
    return {
      priceChartInstance: null as Chart | null,
      volumeChartInstance: null as Chart | null,
      selectedBuilding: null as { label: string; value: string } | null,
      metric: { label: 'Asking Rent', value: 'askingRent' },
      metricOptions: [
        { label: 'Asking Rent', value: 'askingRent' },
        { label: 'Asking Rent PSF', value: 'askingRentPSF' },
        { label: 'Effective Rent', value: 'effectiveRent' },
        { label: 'Effective Rent PSF', value: 'effectiveRentPSF' }
      ]
    }
  },
  computed: {
    buildingOptions() {
      return this.filteredProperties.map((prop) => ({
        label: prop.prop.building_name || prop.prop.street_address,
        value: prop.prop.id
      }))
    },
    aggregationFn() {
      switch (this.metric.value) {
        case 'effectiveRent':
          return (unit: Availability, history: Price) => history.effective_price
        case 'effectiveRentPSF':
          return (unit: Availability, history: Price) =>
            unit.sqft && history.effective_price ? history.effective_price / unit.sqft : null
        case 'askingRent':
          return (unit: Availability, history: Price) => history.price
        case 'askingRentPSF':
          return (unit: Availability, history: Price) =>
            unit.sqft && history.price ? history.price / unit.sqft : null
        default:
          return null
      }
    },
    selectedProperty() {
      if (!this.selectedBuilding) {
        return null
      }
      return (
        this.filteredProperties.find((prop) => prop.prop.id === this.selectedBuilding.value) || null
      )
    },
    chartData() {
      const subjectProp = this.selectedProperty
      if (!subjectProp) {
        return null
      }
      const subject = subjectProp.prop
      const filteredUnits = subjectProp.units

      const maxDate =
        subject.created_on && parseISO(subject.created_on) > this.dateRange.to
          ? this.dateRange.to
          : parseISO(subject.created_on)
      const fromDateStr = format(this.dateRange.from, 'yyyy-MM-dd')
      const toDateStr = format(maxDate, 'yyyy-MM-dd')

      const interval = eachDayOfInterval({ start: this.dateRange.from, end: maxDate })
      const intervalStr = interval.map((date) => format(date, 'yyyy-MM-dd'))

      // priceData contains the price of all units over time (day granularity)
      const priceData: Record<string, { vals: (number | null)[]; color: string; label: string }> =
        {}
      const trafficData = {
        newListings: intervalStr.map(() => 0),
        leaseClosings: intervalStr.map(() => 0)
      }

      const aggregationFn = this.aggregationFn

      for (const { unit } of filteredUnits) {
        if (unit.bed == null || unit.bath == null) {
          continue
        }
        const prices: { created_on: string; history: Price }[] = // sorted by created_on
          this.expandedUnitHistory[unit.id] || []

        // find the index of the first price that is after the fromDateStr
        const startIndex = prices.findIndex((price) => price.created_on >= fromDateStr)

        if (startIndex === -1) {
          continue
        }

        // find the index of the last price that is before the toDateStr
        const endIndex = prices.findLastIndex((price) => price.created_on <= toDateStr)

        if (endIndex === -1) {
          continue
        }

        const firstDate = prices[startIndex].created_on
        const lastDate = prices[endIndex].created_on
        let priceIndex = startIndex

        // Add arrival and closing data
        for (const history of unit.availability_periods) {
          const arrivalDate = history.enter_market
          const closingDate = history.exit_market

          const arrivalIndex = intervalStr.findIndex((date) => date === arrivalDate)
          const closingIndex = intervalStr.findIndex((date) => date === closingDate)

          if (arrivalIndex !== -1) {
            trafficData.newListings[arrivalIndex]++
          }

          if (closingIndex !== -1) {
            trafficData.leaseClosings[closingIndex]++
          }
        }

        const newLine: (number | null)[] = []
        for (const date of intervalStr) {
          if (date < firstDate || date > lastDate) {
            newLine.push(null)
          } else if (date < prices[priceIndex].created_on) {
            newLine.push(null)
          } else {
            newLine.push(aggregationFn(unit, prices[priceIndex].history))
            priceIndex++
          }
        }

        priceData[unit.id] = {
          vals: newLine,
          color: unitTypeColor(unit.bed, 0.3),
          label: unit.unit_name
            ? `Unit ${unit.unit_name}`
            : `N/A - ${unitTypeLabel({ bed: unit.bed, bath: unit.bath, partial_bath: unit.partial_bath })}`
        }
      }
      // Configure the chart with proper scaling and formatting
      const priceChartConfig: ChartConfiguration = {
        data: {
          labels: intervalStr,
          datasets: [
            ...Object.keys(priceData).map((unitId) => {
              const values = priceData[unitId]
              return {
                type: 'line',
                label: values.label,
                data: values.vals,
                borderColor: values.color,
                backgroundColor: values.color
              }
            })
          ]
        },
        options: {
          pointRadius: 0,
          pointHoverRadius: 3,
          interaction: {
            intersect: false
          },
          animation: false,
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              ticks: {
                callback: (value: number) => `$${value.toLocaleString()}`
              },
              title: {
                display: true,
                text: 'Price ($)'
              }
            },
            x: {
              display: false,
              title: {
                display: false,
                text: 'Date'
              }
            }
          },
          plugins: {
            legend: {
              display: false
            },
            decimation: {
              enabled: true
            },
            tooltip: {
              callbacks: {
                // show Unit Name and Date
                label: (context) => {
                  const unitName = context.dataset.label
                  const date = context.label
                  const decimals =
                    this.metric.value === 'effectiveRentPSF' ||
                    this.metric.value === 'askingRentPSF'
                      ? 2
                      : 0
                  return `${unitName} on ${date}: ${formatMoney(context.parsed.y, decimals)}`
                }
              }
            }
          }
        }
      }

      const volumeChartConfig: ChartConfiguration = {
        type: 'bar',
        data: {
          labels: intervalStr,
          datasets: [
            {
              label: 'Volume',
              data: intervalStr.map(
                (_, index) => trafficData.newListings[index] - trafficData.leaseClosings[index]
              )
            }
          ]
        },
        options: {
          borderColor: (context) => {
            const value = context.raw as number
            return value < 0 ? 'rgba(0, 128, 0, 1)' : 'rgba(220, 20, 60, 1)'
          },
          backgroundColor: (context) => {
            const value = context.raw as number
            return value < 0 ? 'rgba(0, 128, 0, 0.8)' : 'rgba(220, 20, 60, 0.8)'
          },
          interaction: {
            intersect: false
          },
          animation: false,
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              ticks: {
                callback: (value) => value.toLocaleString(),
                precision: 0
              },
              title: {
                display: true,
                text: 'Volume'
              }
            }
          },
          plugins: {
            legend: {
              display: false
            }
          }
        }
      }

      return {
        priceChartConfig,
        volumeChartConfig
      }
    }
  },
  watch: {
    chartData() {
      this.createCharts()
    },
    filteredProperties(
      newFilteredProps: { prop: { id: string; building_name: string; street_address: string } }[]
    ) {
      // set selectedBuilding again if the existing selected building is no longer in the filteredProperties
      const selectedBuilding = this.selectedBuilding
      if (
        selectedBuilding &&
        !newFilteredProps.some(
          (prop: { prop: { id: string } }) => prop.prop.id === selectedBuilding.value
        )
      ) {
        this.selectedBuilding = {
          label: newFilteredProps[0].prop.building_name || newFilteredProps[0].prop.street_address,
          value: newFilteredProps[0].prop.id
        }
      }
    }
  },
  mounted() {
    // set selectedBuilding as the subjectDetail or first building
    if (this.filteredProperties.length > 0) {
      this.selectedBuilding = {
        label:
          this.filteredProperties[0].prop.building_name ||
          this.filteredProperties[0].prop.street_address,
        value: this.filteredProperties[0].prop.id
      }
    }
  },
  methods: {
    updateBuilding(newBuilding: { label: string; value: string }) {
      this.$tracker.changedFilter('properties', 'unit_prices_over_time', newBuilding)
    },
    updateMetric(newMetric: { label: string; value: string }) {
      this.$tracker.changedFilter('metric', 'unit_prices_over_time', newMetric)
    },
    createCharts() {
      const priceChart = this.$refs.priceChart as HTMLCanvasElement
      const volumeChart = this.$refs.volumeChart as HTMLCanvasElement

      // destroy existing charts
      if (this.priceChartInstance) {
        this.priceChartInstance.destroy()
      }
      if (this.volumeChartInstance) {
        this.volumeChartInstance.destroy()
      }

      const chartData = this.chartData
      if (!chartData) {
        return null
      }
      const { priceChartConfig, volumeChartConfig } = chartData
      if (!priceChartConfig || !volumeChartConfig) {
        return null
      }

      if (priceChartConfig.data.datasets.length < 1) {
        return null
      }

      this.priceChartInstance = new Chart(priceChart, priceChartConfig)
      this.volumeChartInstance = new Chart(volumeChart, volumeChartConfig)
    }
  }
}
</script>

<style scoped>
.chart-container {
  height: 450px;
  width: 100%;
}
.priceChart {
  width: 100%;
  height: 300px;
}
.volumeChart {
  width: 100%;
  height: 150px;
  padding-left: 14px;
}
</style>
